import { useEffect, useRef, useState } from "react";

import { ANIMATION_DURATION } from "../constants";

export const useStepNavigation = (totalSteps: number = 5) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clearPending = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleNext = () => {
    setIsAnimating(true);
    clearPending();
    timeoutRef.current = setTimeout(() => {
      setCurrentStep((prevStep) => {
        const nextStep = prevStep + 1;
        return nextStep <= totalSteps ? nextStep : prevStep;
      });
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  };

  const handleBack = () => {
    setIsAnimating(true);
    clearPending();
    timeoutRef.current = setTimeout(() => {
      setCurrentStep((prevStep) => {
        const prevStepValue = prevStep - 1;
        return prevStepValue >= 1 ? prevStepValue : prevStep;
      });
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  };

  const handleStepClick = (stepNumber: number) => {
    if (stepNumber <= currentStep) {
      setIsAnimating(true);
      clearPending();
      timeoutRef.current = setTimeout(() => {
        setCurrentStep(stepNumber);
        setIsAnimating(false);
      }, ANIMATION_DURATION);
    }
  };

  const getStepProgress = () => (currentStep / totalSteps) * 100;

  const resetStep = () => {
    setCurrentStep(1);
    clearPending();
    setIsAnimating(false);
  };

  useEffect(() => clearPending, []);
  useEffect(() => {
    setCurrentStep((s) => Math.min(s, totalSteps));
  }, [totalSteps]);

  return {
    currentStep,
    isAnimating,
    handleNext,
    handleBack,
    handleStepClick,
    getStepProgress,
    resetStep,
    canGoNext: currentStep < totalSteps,
    canGoBack: currentStep > 1,
  };
};
